<template>
  <SvoiSelect
    v-bind="$props"
    :options="list"
    :internal-search="false"
    :loading="isFetching"
    @input="val => $emit('input', val)"
    @update:model-value="val => $emit('update:modelValue', val)"
    @blur="$emit('blur')"
    @search-change="debouncedSearchChange"
  >
    <template v-for="(_, slot) of $slots" #[slot]="scope">
      <slot :name="slot" v-bind="scope" />
    </template>
    <template #no-options>
      <slot name="no-options">Список пуст, введите строку для поиска</slot>
    </template>
    <template #after-list>
      <span ref="intersectionRef" />

      <div v-if="isFirstFetching || hasNextPage" class="loader-wrapper">
        <SvoiLoader size="xs" label="" />
      </div>
    </template>
  </SvoiSelect>
</template>

<script lang="ts">
import type { InfiniteSearchCallback, OptionSelect } from '@svoi-ui/interfaces/select'
import type { InfiniteQueryParams } from '@svoi-ui/composables'
import { useInfiniteQuery, useIntersectionRef } from '@svoi-ui/composables'
import type { PropType } from 'vue-demi'
import { computed, defineComponent, ref } from 'vue-demi'
import { debounce } from '@svoi-ui/shared/utils/decorators'
import { SvoiLoader } from '@svoi-ui/components/loader'
import { SvoiSelect } from '.'

export default defineComponent({
  name: 'SvoiAsyncSelect',
  components: { SvoiLoader, SvoiSelect },
  props: {
    ...SvoiSelect.props,
    searchCallback: {
      type: Function as PropType<InfiniteSearchCallback>,
      required: true
    },
    delay: {
      type: Number,
      default: 0
    },
    infiniteList: {
      type: Object as PropType<InfiniteQueryParams>,
      default: () => ({ page: 1, count: 10 })
    }
  },
  emits: ['input', 'blur', 'update:modelValue'],
  setup(props) {
    const searchValue = ref<string>('')

    const intersectionRef = ref<HTMLSpanElement>()

    const {
      isFetching,
      isFirstFetching,
      fetchNextPage,
      fetchPage,
      hasNextPage,
      data: list
    } = useInfiniteQuery<OptionSelect>({
      query: ({ count, page }) => props.searchCallback(searchValue.value, { count, page }),
      queryParams: props.infiniteList,
      immediate: true
    })

    useIntersectionRef({
      intersectionRef,
      onIntersection: fetchNextPage
    })

    const onSearchChange = (query?: string) => {
      searchValue.value = query ?? ''

      fetchPage()
    }

    const debouncedSearchChange = computed(() =>
      props.delay > 0 ? debounce(onSearchChange, props.delay) : onSearchChange
    )

    return {
      intersectionRef,
      list,
      isFirstFetching,
      hasNextPage,
      isFetching,
      searchValue,
      debouncedSearchChange,
      fetchNextPage,
      fetchPage
    }
  }
})
</script>

<style lang="scss" scoped>
.loader-wrapper {
  display: flex;
  align-items: center;
  padding: 12px;
}
</style>
